import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, Typography, Chip, Skeleton, Divider, Avatar, Tooltip,
  useTheme, alpha, IconButton, Collapse, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import {
  Close, SportsBaseball, CompareArrows, Person, TrendingUp,
  TrendingDown, Remove, History, CalendarToday,
} from '@mui/icons-material';
import { matchupsApi } from '../services/apiService';
import { getTeamLogoUrl as getSharedTeamLogoUrl } from '../utils/teamLogos';

const getTeamLogoUrl = (tc) => getSharedTeamLogoUrl(tc, 500);

const fmtAvg = (v) => (v != null && !isNaN(v)) ? parseFloat(v).toFixed(3).replace(/^0/, '') : '—';
const fmtDec = (v, d = 2) => (v != null && !isNaN(v)) ? parseFloat(v).toFixed(d) : '—';
const fmtPct = (v) => (v != null && !isNaN(v)) ? (parseFloat(v) * 100).toFixed(0) + '%' : '—';
const fmtInt = (v) => (v != null && !isNaN(v)) ? Math.round(v).toString() : '—';

// Color gradient for OPS values (batters)
const opsColor = (ops) => {
  if (ops == null || isNaN(ops)) return '#888';
  if (ops >= 1.000) return '#ff6b35';
  if (ops >= 0.850) return '#ff8c42';
  if (ops >= 0.750) return '#ffd23f';
  if (ops >= 0.650) return '#06ffa5';
  if (ops >= 0.550) return '#4fb3d9';
  return '#c0392b';
};

// Trend indicator for comparing OPS vs baseline
const OpsTrend = ({ ops, baseline }) => {
  if (ops == null || baseline == null) return null;
  const diff = ops - baseline;
  if (Math.abs(diff) < 0.020) return <Remove sx={{ fontSize: 12, color: '#888' }} />;
  if (diff > 0) return <TrendingUp sx={{ fontSize: 12, color: '#06ffa5' }} />;
  return <TrendingDown sx={{ fontSize: 12, color: '#c0392b' }} />;
};

/**
 * MatchupPreview – shows batter-vs-pitcher matchup data for a game.
 *
 * Props:
 *  - game: full game object from UpcomingSchedule { opponent, probablePitchers, isHome, homeAway, ... }
 *  - teamId: our team abbreviation
 *  - teamColor: hex accent color
 *  - onClose: callback to dismiss
 *  - season: current season year
 */
const MatchupPreview = ({ game, teamId, teamColor = '#1976d2', onClose, season = '2025' }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [matchupData, setMatchupData] = useState(null);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('career'); // 'career' or 'season'

  const fetchMatchup = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const oppPitcher = game.isHome ? game.probablePitchers?.away : game.probablePitchers?.home;
      const ourPitcher = game.isHome ? game.probablePitchers?.home : game.probablePitchers?.away;
      const opponent = game.opponent;

      if (!oppPitcher?.name) {
        setMatchupData({ batters: [], careerBatters: [], vsTeam: [], careerVsTeam: [], pitcherMatch: null, noData: true, reason: 'No opposing pitcher announced' });
        return;
      }

      // Fetch game preview: our batters vs their pitcher (now returns career data too)
      const preview = await matchupsApi.getGamePreview(teamId, opponent, oppPitcher.name, season);

      // Also fetch how our pitcher does against their lineup (if we know our pitcher)
      let pitcherMatch = null;
      if (ourPitcher?.name) {
        try {
          pitcherMatch = await matchupsApi.getPitcherVsLineup(teamId, ourPitcher.name, opponent, season);
        } catch { /* no data is fine */ }
      }

      setMatchupData({
        batters: preview.batters || [],
        careerBatters: preview.careerBatters || [],
        vsTeam: preview.vsTeam || [],
        careerVsTeam: preview.careerVsTeam || [],
        pitcherMatch,
        oppPitcher: oppPitcher.name,
        ourPitcher: ourPitcher?.name,
        noData: false,
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [game, teamId, season]);

  useEffect(() => { fetchMatchup(); }, [fetchMatchup]);

  if (loading) {
    return (
      <Card elevation={2} sx={{ border: `1px solid ${alpha(teamColor, 0.3)}`, borderRadius: 2, p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Skeleton variant="circular" width={20} height={20} />
          <Skeleton variant="text" width={200} />
          <Box sx={{ flex: 1 }} />
          <Skeleton variant="circular" width={24} height={24} />
        </Box>
        {[1, 2, 3, 4].map(i => (
          <Skeleton key={i} variant="rectangular" height={28} sx={{ borderRadius: 1, mb: 0.5 }} />
        ))}
      </Card>
    );
  }

  if (error) {
    return (
      <Card elevation={2} sx={{ border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`, borderRadius: 2, p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="error">Failed to load matchup data</Typography>
          <IconButton size="small" onClick={onClose}><Close sx={{ fontSize: 16 }} /></IconButton>
        </Box>
      </Card>
    );
  }

  const { batters, careerBatters, vsTeam, careerVsTeam, pitcherMatch, oppPitcher, ourPitcher, noData, reason } = matchupData || {};

  // Determine which data to show based on view mode
  const activeBatters = viewMode === 'career' ? (careerBatters || []) : (batters || []);
  const activeVsTeam = viewMode === 'career' ? (careerVsTeam || []) : (vsTeam || []);
  const activePitcherBatters = viewMode === 'career'
    ? (pitcherMatch?.careerBatters || [])
    : (pitcherMatch?.batters || []);
  const hasCareerData = (careerBatters?.length || 0) > 0;
  const hasSeasonData = (batters?.length || 0) > 0;

  return (
    <Card
      elevation={2}
      sx={{
        border: `1px solid ${alpha(teamColor, 0.3)}`,
        borderRadius: 2,
        mb: 2,
        overflow: 'hidden',
        background: `linear-gradient(135deg, ${alpha(teamColor, 0.02)}, transparent)`,
      }}
    >
      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 2, py: 1.5,
        borderBottom: `1px solid ${alpha(teamColor, 0.1)}`,
        background: alpha(teamColor, 0.04),
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CompareArrows sx={{ fontSize: 18, color: teamColor }} />
          <Typography variant="subtitle2" fontWeight={700} fontSize="0.8rem">
            Matchup Preview
          </Typography>
          <Chip
            label={`${teamId} vs ${game.opponent}`}
            size="small"
            avatar={<Avatar src={getTeamLogoUrl(game.opponent)} sx={{ width: 16, height: 16 }} />}
            sx={{ height: 22, fontSize: '0.75rem', fontWeight: 600 }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, v) => v && setViewMode(v)}
            size="small"
            sx={{
              height: 24,
              '& .MuiToggleButton-root': {
                px: 1, py: 0, fontSize: '0.75rem', fontWeight: 700,
                textTransform: 'none', borderRadius: 1,
                '&.Mui-selected': { bgcolor: alpha(teamColor, 0.15), color: teamColor },
              },
            }}
          >
            <ToggleButton value="career">
              <History sx={{ fontSize: 12, mr: 0.3 }} /> Career
            </ToggleButton>
            <ToggleButton value="season">
              <CalendarToday sx={{ fontSize: 11, mr: 0.3 }} /> {season}
            </ToggleButton>
          </ToggleButtonGroup>
          <IconButton size="small" onClick={onClose} sx={{ p: 0.25 }}>
            <Close sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ p: 2 }}>
        {noData ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            {reason || 'No matchup data available'}
          </Typography>
        ) : (
          <>
            {/* Section 1: Our Batters vs Their Pitcher */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                <SportsBaseball sx={{ fontSize: 14, color: teamColor }} />
                <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.75rem', textTransform: 'uppercase', color: teamColor }}>
                  {teamId} Batters vs {oppPitcher} {viewMode === 'career' ? '(Career)' : `(${season})`}
                </Typography>
                {activeBatters.length > 0 && (
                  <Chip label={`${activeBatters.length} with history`} size="small"
                    sx={{ height: 18, fontSize: '0.75rem', ml: 0.5 }} />
                )}
              </Box>

              {activeBatters.length > 0 ? (
                <TableContainer sx={{ maxHeight: 260 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, py: 0.5, pl: 1 }}>Batter</TableCell>
                        <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 700, py: 0.5 }}>PA</TableCell>
                        <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 700, py: 0.5 }}>AVG</TableCell>
                        <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 700, py: 0.5 }}>OBP</TableCell>
                        <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 700, py: 0.5 }}>SLG</TableCell>
                        <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 700, py: 0.5 }}>OPS</TableCell>
                        <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 700, py: 0.5 }}>HR</TableCell>
                        <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 700, py: 0.5 }}>K%</TableCell>
                        <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 700, py: 0.5 }}>BB%</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {activeBatters.map((b, i) => {
                        const s = b.stats;
                        return (
                          <TableRow key={i} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                            <TableCell sx={{ fontSize: '0.75rem', py: 0.4, pl: 1, fontWeight: 600, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {b.name}
                              {viewMode === 'career' && b.seasonCount > 1 && (
                                <Typography component="span" variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', ml: 0.5 }}>
                                  {b.seasonCount}yr
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: '0.75rem', py: 0.4 }}>
                              {fmtInt(s.plateAppearances)}
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: '0.75rem', py: 0.4, fontWeight: 600 }}>
                              {fmtAvg(s.avg)}
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: '0.75rem', py: 0.4 }}>
                              {fmtAvg(s.obp)}
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: '0.75rem', py: 0.4 }}>
                              {fmtAvg(s.slg)}
                            </TableCell>
                            <TableCell align="center" sx={{
                              fontSize: '0.75rem', py: 0.4, fontWeight: 700,
                              color: opsColor(s.ops),
                            }}>
                              {fmtAvg(s.ops)}
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: '0.75rem', py: 0.4 }}>
                              {fmtInt(s.homeRuns)}
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: '0.75rem', py: 0.4 }}>
                              {fmtPct(s.kRate)}
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: '0.75rem', py: 0.4 }}>
                              {fmtPct(s.bbRate)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', py: 1.5 }}>
                  {viewMode === 'career'
                    ? 'No career history found against this pitcher'
                    : `No history found this season — ${hasCareerData ? 'try Career view' : 'first time facing this pitcher'}`}
                </Typography>
              )}
            </Box>

            {/* Section 2: Our Batters vs Their Team (broader sample) */}
            {activeVsTeam && activeVsTeam.length > 0 && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Box sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                    <Person sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.75rem', textTransform: 'uppercase', color: 'text.secondary' }}>
                      {teamId} Batters vs {game.opponent} (All Pitchers{viewMode === 'career' ? ', Career' : ''})
                    </Typography>
                    <Chip label={`${activeVsTeam.length} batters`} size="small"
                      sx={{ height: 18, fontSize: '0.75rem', ml: 0.5 }} />
                  </Box>

                  <TableContainer sx={{ maxHeight: 220 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, py: 0.5, pl: 1 }}>Batter</TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 700, py: 0.5 }}>PA</TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 700, py: 0.5 }}>AVG</TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 700, py: 0.5 }}>OPS</TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 700, py: 0.5 }}>HR</TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 700, py: 0.5 }}>H</TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 700, py: 0.5 }}>RBI</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {activeVsTeam.slice(0, 12).map((b, i) => {
                          const s = b.stats;
                          return (
                            <TableRow key={i} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                              <TableCell sx={{ fontSize: '0.75rem', py: 0.4, pl: 1, fontWeight: 600, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {b.name}
                              </TableCell>
                              <TableCell align="center" sx={{ fontSize: '0.75rem', py: 0.4 }}>
                                {fmtInt(s.plateAppearances)}
                              </TableCell>
                              <TableCell align="center" sx={{ fontSize: '0.75rem', py: 0.4, fontWeight: 600 }}>
                                {fmtAvg(s.avg)}
                              </TableCell>
                              <TableCell align="center" sx={{
                                fontSize: '0.75rem', py: 0.4, fontWeight: 700,
                                color: opsColor(s.ops),
                              }}>
                                {fmtAvg(s.ops)}
                              </TableCell>
                              <TableCell align="center" sx={{ fontSize: '0.75rem', py: 0.4 }}>
                                {fmtInt(s.homeRuns)}
                              </TableCell>
                              <TableCell align="center" sx={{ fontSize: '0.75rem', py: 0.4 }}>
                                {fmtInt(s.hits)}
                              </TableCell>
                              <TableCell align="center" sx={{ fontSize: '0.75rem', py: 0.4 }}>
                                {fmtInt(s.rbi)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </>
            )}

            {/* Section 3: Our Pitcher vs Their Batters */}
            {pitcherMatch && pitcherMatch.found && activePitcherBatters?.length > 0 && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                    <SportsBaseball sx={{ fontSize: 14, color: theme.palette.info.main }} />
                    <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.75rem', textTransform: 'uppercase', color: theme.palette.info.main }}>
                      {ourPitcher} vs {game.opponent} Batters {viewMode === 'career' ? '(Career)' : `(${season})`}
                    </Typography>
                    <Chip label={`${activePitcherBatters.length} batters faced`} size="small"
                      sx={{ height: 18, fontSize: '0.75rem', ml: 0.5 }} />
                  </Box>

                  <TableContainer sx={{ maxHeight: 200 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, py: 0.5, pl: 1 }}>Batter</TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 700, py: 0.5 }}>PA</TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 700, py: 0.5 }}>AVG</TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 700, py: 0.5 }}>OBP</TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 700, py: 0.5 }}>HR</TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 700, py: 0.5 }}>H</TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 700, py: 0.5 }}>K%</TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 700, py: 0.5 }}>BB%</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {activePitcherBatters.map((b, i) => {
                          const s = b.stats;
                          return (
                            <TableRow key={i} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                              <TableCell sx={{ fontSize: '0.75rem', py: 0.4, pl: 1, fontWeight: 600, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {b.name}
                              </TableCell>
                              <TableCell align="center" sx={{ fontSize: '0.75rem', py: 0.4 }}>
                                {fmtInt(s.plateAppearances)}
                              </TableCell>
                              <TableCell align="center" sx={{ fontSize: '0.75rem', py: 0.4, fontWeight: 600 }}>
                                {fmtAvg(s.avg)}
                              </TableCell>
                              <TableCell align="center" sx={{ fontSize: '0.75rem', py: 0.4 }}>
                                {fmtAvg(s.obp)}
                              </TableCell>
                              <TableCell align="center" sx={{ fontSize: '0.75rem', py: 0.4 }}>
                                {fmtInt(s.homeRuns)}
                              </TableCell>
                              <TableCell align="center" sx={{ fontSize: '0.75rem', py: 0.4 }}>
                                {fmtInt(s.hits)}
                              </TableCell>
                              <TableCell align="center" sx={{ fontSize: '0.75rem', py: 0.4 }}>
                                {fmtPct(s.kRate)}
                              </TableCell>
                              <TableCell align="center" sx={{ fontSize: '0.75rem', py: 0.4 }}>
                                {fmtPct(s.bbRate)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </>
            )}
          </>
        )}
      </Box>
    </Card>
  );
};

export default MatchupPreview;
